import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import httpStatus from 'http-status';
import { toast } from 'react-hot-toast';
import callApi from '../../utils/call-api.js';
import analyzeApiResponse from '../../utils/analyze-api-response.js';

function formatLoggedBy(reading) {
    if (reading.loggedByDisplay) {
        return reading.loggedByDisplay;
    }
    const lb = reading.loggedBy;
    if (!lb) {
        return null;
    }
    const name = [lb.firstName, lb.lastName].filter(Boolean).join(' ').trim();
    return name || lb.email || null;
}

function toDateInputValue(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
}

export default function KpiReadingLog({
    kpiId,
    canLog,
    canEditTarget,
    target,
    reportingYear,
    onChanged
}) {
    const [readings, setReadings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [valueDraft, setValueDraft] = useState('');
    const [dateDraft, setDateDraft] = useState(toDateInputValue(new Date()));
    const [noteDraft, setNoteDraft] = useState('');
    const [targetDraft, setTargetDraft] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editDate, setEditDate] = useState('');
    const [editNote, setEditNote] = useState('');

    const loadReadings = useCallback(async () => {
        if (!kpiId) return;
        setLoading(true);
        try {
            const res = await callApi(
                `/executive-pulse/kpis/${encodeURIComponent(kpiId)}/readings`,
                'get'
            );
            if (!res.ok) {
                const msg = await analyzeApiResponse(res);
                toast.error(msg || 'Could not load log history.');
                return;
            }
            const data = await res.json();
            setReadings(Array.isArray(data.readings) ? data.readings : []);
        } catch (e) {
            toast.error(e?.message || 'Could not load log history.');
        } finally {
            setLoading(false);
        }
    }, [kpiId]);

    useEffect(() => {
        loadReadings();
    }, [loadReadings]);

    useEffect(() => {
        setTargetDraft(
            typeof target === 'number' && !Number.isNaN(target)
                ? String(target)
                : ''
        );
    }, [target]);

    const handleLog = async () => {
        const num = Number.parseFloat(valueDraft);
        if (Number.isNaN(num)) {
            toast.error('Enter a valid value.');
            return;
        }
        if (!dateDraft) {
            toast.error('Pick a date for this log entry.');
            return;
        }

        setSaving(true);
        try {
            const res = await callApi(
                `/executive-pulse/kpis/${encodeURIComponent(kpiId)}/readings`,
                'post',
                {
                    value: num,
                    recordedAt: new Date(dateDraft).toISOString(),
                    note: noteDraft
                }
            );
            if (res.status === httpStatus.FORBIDDEN) {
                toast.error('You do not have permission to log this metric.');
                return;
            }
            if (!res.ok) {
                const msg = await analyzeApiResponse(res);
                toast.error(msg || 'Could not save log.');
                return;
            }
            toast.success('Logged.');
            setValueDraft('');
            setNoteDraft('');
            setDateDraft(toDateInputValue(new Date()));
            await loadReadings();
            onChanged?.();
        } catch (e) {
            toast.error(e?.message || 'Could not save log.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveTarget = async () => {
        const targetNum = Number.parseFloat(targetDraft);
        if (Number.isNaN(targetNum)) {
            toast.error('Enter a valid goal.');
            return;
        }
        setSaving(true);
        try {
            const qs = new URLSearchParams({
                year: String(reportingYear)
            });
            const res = await callApi(
                `/executive-pulse/manual-kpis/${encodeURIComponent(
                    kpiId
                )}?${qs.toString()}`,
                'patch',
                { target: targetNum }
            );
            if (!res.ok) {
                const msg = await analyzeApiResponse(res);
                toast.error(msg || 'Could not save goal.');
                return;
            }
            toast.success('Goal saved.');
            onChanged?.();
        } catch (e) {
            toast.error(e?.message || 'Could not save goal.');
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (reading) => {
        if (reading.source !== 'manual') return;
        setEditingId(reading.id);
        setEditValue(String(reading.value));
        setEditDate(toDateInputValue(reading.recordedAt));
        setEditNote(reading.note ?? '');
    };

    const cancelEdit = () => {
        setEditingId(null);
    };

    const handleUpdate = async (readingId) => {
        const num = Number.parseFloat(editValue);
        if (Number.isNaN(num)) {
            toast.error('Enter a valid value.');
            return;
        }
        setSaving(true);
        try {
            const res = await callApi(
                `/executive-pulse/kpis/${encodeURIComponent(
                    kpiId
                )}/readings/${encodeURIComponent(readingId)}`,
                'patch',
                {
                    value: num,
                    recordedAt: new Date(editDate).toISOString(),
                    note: editNote
                }
            );
            if (!res.ok) {
                const msg = await analyzeApiResponse(res);
                toast.error(msg || 'Could not update.');
                return;
            }
            toast.success('Updated.');
            setEditingId(null);
            await loadReadings();
            onChanged?.();
        } catch (e) {
            toast.error(e?.message || 'Could not update.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (readingId) => {
        if (!window.confirm('Delete this log entry?')) return;
        setSaving(true);
        try {
            const res = await callApi(
                `/executive-pulse/kpis/${encodeURIComponent(
                    kpiId
                )}/readings/${encodeURIComponent(readingId)}`,
                'delete'
            );
            if (!res.ok) {
                const msg = await analyzeApiResponse(res);
                toast.error(msg || 'Could not delete.');
                return;
            }
            toast.success('Deleted.');
            await loadReadings();
            onChanged?.();
        } catch (e) {
            toast.error(e?.message || 'Could not delete.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="kpi-reading-log">
            {canLog ? (
                <div className="modal-manual-edit">
                    <h4 className="modal-manual-edit-title">Log update</h4>
                    <p className="modal-manual-edit-hint">
                        Values are saved with the date you choose (backdating
                        allowed). Use 0–100 for percent-complete KPIs.
                    </p>
                    <div className="kpi-reading-log-form">
                        <div className="modal-manual-edit-row">
                            <label htmlFor="exec-pulse-log-value">Value</label>
                            <input
                                id="exec-pulse-log-value"
                                type="number"
                                step="any"
                                className="modal-manual-edit-input"
                                value={valueDraft}
                                onChange={(e) => setValueDraft(e.target.value)}
                            />
                        </div>
                        <div className="modal-manual-edit-row">
                            <label htmlFor="exec-pulse-log-date">Date</label>
                            <input
                                id="exec-pulse-log-date"
                                type="date"
                                className="modal-manual-edit-input"
                                value={dateDraft}
                                onChange={(e) => setDateDraft(e.target.value)}
                            />
                        </div>
                        <div className="modal-manual-edit-row">
                            <label htmlFor="exec-pulse-log-note">
                                Note (optional)
                            </label>
                            <input
                                id="exec-pulse-log-note"
                                type="text"
                                className="modal-manual-edit-input kpi-reading-log-note"
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                            />
                        </div>
                        <button
                            type="button"
                            className="modal-manual-edit-btn primary"
                            disabled={saving}
                            onClick={handleLog}>
                            {saving ? 'Saving…' : 'Log value'}
                        </button>
                    </div>
                </div>
            ) : null}

            {canEditTarget ? (
                <div className="modal-manual-edit">
                    <h4 className="modal-manual-edit-title">
                        Goal for {reportingYear}
                    </h4>
                    <div className="modal-manual-edit-row">
                        <label htmlFor="exec-pulse-log-target">Target</label>
                        <input
                            id="exec-pulse-log-target"
                            type="number"
                            step="any"
                            className="modal-manual-edit-input"
                            value={targetDraft}
                            onChange={(e) => setTargetDraft(e.target.value)}
                        />
                    </div>
                    <button
                        type="button"
                        className="modal-manual-edit-btn primary"
                        disabled={saving}
                        onClick={handleSaveTarget}>
                        Save goal
                    </button>
                </div>
            ) : null}

            <div className="kpi-reading-log-history">
                <h4 className="modal-manual-edit-title">Activity log</h4>
                {loading ? (
                    <p className="modal-manual-edit-hint">Loading…</p>
                ) : null}
                {!loading && readings.length === 0 ? (
                    <p className="modal-manual-edit-hint">
                        No logged entries yet.
                    </p>
                ) : null}
                <ul className="kpi-reading-log-list">
                    {readings.map((r) => {
                        const loggedByLabel = formatLoggedBy(r);
                        return (
                            <li key={r.id} className="kpi-reading-log-item">
                                {editingId === r.id ? (
                                    <div className="kpi-reading-log-edit">
                                        <input
                                            type="number"
                                            step="any"
                                            className="modal-manual-edit-input"
                                            value={editValue}
                                            onChange={(e) =>
                                                setEditValue(e.target.value)
                                            }
                                        />
                                        <input
                                            type="date"
                                            className="modal-manual-edit-input"
                                            value={editDate}
                                            onChange={(e) =>
                                                setEditDate(e.target.value)
                                            }
                                        />
                                        <input
                                            type="text"
                                            className="modal-manual-edit-input"
                                            value={editNote}
                                            placeholder="Note"
                                            onChange={(e) =>
                                                setEditNote(e.target.value)
                                            }
                                        />
                                        <div className="kpi-reading-log-edit-actions">
                                            <button
                                                type="button"
                                                className="modal-manual-edit-btn primary"
                                                disabled={saving}
                                                onClick={() =>
                                                    handleUpdate(r.id)
                                                }>
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                className="modal-manual-edit-btn secondary"
                                                onClick={cancelEdit}>
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="kpi-reading-log-item-main">
                                            <strong>{r.value}</strong>
                                            <span>
                                                {new Date(
                                                    r.recordedAt
                                                ).toLocaleDateString()}
                                            </span>
                                            {loggedByLabel ? (
                                                <span className="kpi-reading-log-source">
                                                    Logged by {loggedByLabel}
                                                </span>
                                            ) : null}
                                        </div>
                                        {r.note ? (
                                            <p className="kpi-reading-log-item-note">
                                                {r.note}
                                            </p>
                                        ) : null}
                                        {r.source === 'manual' ? (
                                            <div className="kpi-reading-log-item-actions">
                                                <button
                                                    type="button"
                                                    className="kpi-reading-log-link"
                                                    onClick={() =>
                                                        startEdit(r)
                                                    }>
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className="kpi-reading-log-link kpi-reading-log-link--danger"
                                                    onClick={() =>
                                                        handleDelete(r.id)
                                                    }>
                                                    Delete
                                                </button>
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

KpiReadingLog.propTypes = {
    kpiId: PropTypes.string,
    canLog: PropTypes.bool,
    canEditTarget: PropTypes.bool,
    target: PropTypes.number,
    reportingYear: PropTypes.number.isRequired,
    onChanged: PropTypes.func
};

KpiReadingLog.defaultProps = {
    kpiId: undefined,
    canLog: false,
    canEditTarget: false,
    target: undefined,
    onChanged: undefined
};
